import React, { FunctionComponent, useState } from "react";
import { get as _get } from "lodash";
import axios from "axios";
import { AuditResult } from "/src/domain/challenges/ChallengeAuditData";
import { StartNumber } from "/src/domain/challenges/ChallengeProtocolData"
import {
  getProtocol,
  ProtocolData,
} from "/src/domain/shared/requests/getProtocol";
import {parse} from 'query-string';
import { isString as _isString } from "lodash";
import { links } from '/src/domain/shared/links';

export interface ChallengeContextProps {
  currentAudit: AuditResult | null;
  audit: (challengeKey: string, userId: string) => Promise<void>;
  vote: (challengeId: number, userId: string, voteId: string) => Promise<void>;
  progress: boolean;
  error: string | null;
  chosenClub: string | null;
  changeChosenClub: (clubName: string | null) => void;
  protocol?: ProtocolData | null;
  requestProtocolData: (challengeKey?: string) => Promise<void>;
  startNumbers?: StartNumber[] | null;
  requestStartNumber: (userId: string) => Promise<void>;
}

const getClubId = () => {
  const params = parse(location.search);
  const clubId = params['club-id'];
  return _isString(clubId) ? clubId : null;
}

export const ChallengeContext = React.createContext<
  ChallengeContextProps | undefined
>(undefined);

export const ChallengeProvider: FunctionComponent = ({ children }) => {
  const [currentAudit, setAuditResult] = useState<AuditResult | null>(null);
  const [progress, setProgress] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [chosenClub, setChosenClub] = useState<string | null>(getClubId);
  const [protocol, setProtocol] = useState<ProtocolData | null>();
  const [startNumbers, setStartNumbers] = useState<StartNumber[] | null>();

  const requestAuditData = async (
    challengeKey: string,
    userId: string
  ): Promise<void> => {
    setProgress(true);
    try {
      const response = await axios.get(
        `${links.challengesOrigin}/api/challenges/audit-v2`,
        {
          withCredentials: true,
          params: {
            challenge_key: challengeKey,
            user_id: userId,
          },
        }
      );
      setAuditResult(response.data.result);
      setAuditError(null);
    } catch (e) {
      if (_get(e, "response.status") === 400) {
        setAuditError("unableCheckUserIdError");
      } else {
        setAuditError("serverError");
      }
      setAuditResult(null);
    }
    setProgress(false);
  };

  const changeChosenClub = (clubId: string | null) => {
    const newUrl = new URL(window.location.href);
    const params = newUrl.searchParams;
    if (clubId) {
      params.set('club-id', clubId);
    } else {
      params.delete('club-id');
    }
    window.history.pushState(null, '', newUrl.toString());
    setChosenClub(clubId);
  }

  const vote = async (
    challengeId: number,
    userId: string,
    voteId: string
  ): Promise<void> => {
    const formData = new FormData();
    formData.append("challengeId", challengeId.toString());
    formData.append("userId", userId);
    formData.append("voteId", voteId);

    try {
      await axios.post(
        `${links.challengesOrigin}/api/challenges/vote`,
        formData
      );
    } catch (e) {}
    window.location.reload();
  };

  const requestProtocolData = async (challengeKey?: string) => {
    const protocol = await getProtocol(challengeKey);
    setProtocol(protocol);
  };

  const requestStartNumber = async (userId: string) => {
    try {
      const response = await axios.get(
        `${links.challengesOrigin}/api/challenges/registration/start-number`,
        {
          params: {
            user_id: userId,
          },
        }
      );
      setStartNumbers(response.data || null);
    } catch (e) {
      console.log('requestStartNumber', e);
    }
  }

  return (
    <ChallengeContext.Provider
      value={{
        currentAudit,
        audit: requestAuditData,
        vote,
        progress,
        error: auditError,
        chosenClub,
        changeChosenClub,
        protocol,
        requestProtocolData,
        startNumbers,
        requestStartNumber,
      }}
    >
      {children}
    </ChallengeContext.Provider>
  );
};
